import type { Campaign, Config, LogicDefinition, Variant, Widget } from '@dashboard/types/config'
import type { ConfigChange } from '@dashboard/composables/useConfigChanges'
import type { HostedConfigChange } from '@shared/configChanges'
import { buttonGroups, colorGroups, designSections } from '@dashboard/modules/widget/components/v2/design/designOptions'
import {
  DISPLAY_DEVICES,
  DISPLAY_TRIGGERS,
  FLOW_QUESTION_CHOICES,
  decodeDisplayTrigger,
  describeDataLayerTriggerEvent,
  flowQuestionChoice
} from '@dashboard/modules/widget/services/widgetDisplay'
import type { DataLayerTriggerEvent } from '@dashboard/modules/widget/services/widgetDisplay'
import { widgetTypeLabel } from '@dashboard/modules/widget/services/widgetEditorNavigation'
import { countLogicDefinitionConditions } from '@dashboard/modules/segment/services/segmentUtilities'
import { summarizePageTargeting } from '@shared/targeting/pageConditions'
import type { TargetingCondition } from '@shared/targeting/pageConditions'
import { stripHtml } from '@shared/services/text'
import { LINK_ACTION_LABELS } from '@dashboard/modules/offer/services/linkActionLabels'
import { describeFieldChanges, humanizeKey } from './publishFieldChanges'
import type { EntityNames, FieldChange, FieldDescriber, FieldVocabulary } from './publishFieldChanges'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import type { FunnelDefinitionRootNode, NodeEmail } from '@dashboard/modules/flow/builder/types/nodes'
import { audienceDisplayName, buildGroupCatalog } from '@dashboard/modules/personalization/services/editor/groups'
import { audienceTerms } from '@dashboard/modules/personalization/services/editor/campaignEdit'
import type { StoredAction } from '@dashboard/modules/personalization/services/editor/campaignEdit'
import { phrasePlainText } from './phraseText'
import {
  ORIGINAL_ID,
  ORIGINAL_NAME,
  audienceVersions,
  effectiveActions,
  testNode,
  type AudienceTest
} from '@shared/campaignTests/audienceTest'
import { versionLabel } from '@shared/campaignTests/versionLabel'
import { fillConfidenceLabel } from '@dashboard/modules/segment/services/questionFill'
import { segmentName } from '@shared/services/segmentName'

/** A summary row: both sides go through `summarize`; identical summaries read as "Changed". */
function summaryDescriber(field: string, summarize: (value: unknown) => string): FieldDescriber {
  return (before, after) => {
    const [was, now] = [summarize(before), summarize(after)]
    return [was === now ? { field, before: null, after: null } : { field, before: was, after: now }]
  }
}

// Worded like the Auto-Segment tab's condition counts; stored config already matches `LogicDefinition`.
function conditionSummary(value: unknown, empty: string): string {
  const count = value ? countLogicDefinitionConditions(value as LogicDefinition) : 0
  return count ? `${count} ${count === 1 ? 'condition' : 'conditions'}` : empty
}

function countSteps(value: unknown): number {
  if (Array.isArray(value)) {
    return value.reduce<number>((total, item) => total + countSteps(item), 0)
  }
  if (!value || typeof value !== 'object') {
    return 0
  }
  const own = 'type' in value && typeof value.type === 'string' && value.type !== 'root' ? 1 : 0
  return Object.values(value).reduce<number>((total, item) => total + countSteps(item), own)
}

const emailNodeVocabulary: FieldVocabulary = {
  labels: { to: 'To', subject: 'Subject', body: 'Body', fireOnce: 'Send once per visitor' },
  hidden: { type: true }
}

function emailNodes(value: unknown): Map<string, NodeEmail> {
  const nodes = new Map<string, NodeEmail>()
  if (value && typeof value === 'object' && 'type' in value && value.type === 'root') {
    funnelDefinitionEach(value as FunnelDefinitionRootNode, (node) => {
      if (node.type === 'email') {
        nodes.set(node._uuid, node)
      }
    })
  }
  return nodes
}

const describeFlowSteps: FieldDescriber = (before, after, names) => {
  const rows = summaryDescriber('Steps', (value) => {
    const steps = countSteps(value)
    return `${steps} ${steps === 1 ? 'step' : 'steps'}`
  })(before, after, names)
  const previous = emailNodes(before)
  for (const [id, node] of emailNodes(after)) {
    const was = previous.get(id)
    if (was) {
      rows.push(
        ...describeFieldChanges(was, node, names, emailNodeVocabulary).map((row) => ({
          ...row,
          field: `Send email notification › ${row.field}`
        }))
      )
    }
  }
  return rows
}

// Segment rules (`signals`) are worded per segment, as on the Auto-Segment tab:
// "Rules for Auckland: Only when asked → Visited /auckland".
const describeSegmentRules: FieldDescriber = (before, after, names) => {
  const bySegment = (signals: unknown) =>
    new Map(
      (Array.isArray(signals) ? signals : []).flatMap((signal: unknown) =>
        signal && typeof signal === 'object' && 'indicates' in signal && typeof signal.indicates === 'string'
          ? [[signal.indicates, 'definition' in signal ? signal.definition : null] as const]
          : []
      )
    )
  const was = bySegment(before)
  const now = bySegment(after)
  const rows: FieldChange[] = []
  for (const [segmentId, current] of now) {
    const previous = was.get(segmentId)
    if (JSON.stringify(previous) === JSON.stringify(current)) {
      continue
    }
    const isNewSegment = !was.has(segmentId)
    const [beforeText, afterText] = [
      conditionSummary(previous, 'Only when asked'),
      conditionSummary(current, 'Only when asked')
    ]
    // A new segment is already its own row; only rules it starts with add anything.
    if (isNewSegment && afterText === 'Only when asked') {
      continue
    }
    const field = `Rules for ${names.get(segmentId) ?? 'a segment'}`
    rows.push(
      beforeText === afterText
        ? { field, before: null, after: null }
        : { field, before: isNewSegment ? null : beforeText, after: afterText }
    )
  }
  return rows
}

function dismissSummary(rule: unknown): string {
  if (!rule || typeof rule !== 'object' || !('type' in rule)) {
    return 'Hidden until their next visit'
  }
  if (rule.type === 'forever') {
    return 'Hidden forever'
  }
  if (rule.type === 'duration') {
    const duration = 'duration' in rule ? String(rule.duration) : '1'
    const unit = 'unit' in rule ? String(rule.unit) : 'hours'
    return `Hidden for ${duration} ${unit}`
  }
  return 'Hidden until their next visit'
}

function triggerSummary(trigger: unknown): string {
  const { type, value } = decodeDisplayTrigger(typeof trigger === 'string' ? trigger : undefined)
  if (type === 'time') {
    return `After ${value} seconds`
  }
  if (type === 'scroll') {
    return `After scrolling ${value}%`
  }
  return DISPLAY_TRIGGERS.find((option) => option.value === type)?.label ?? 'Immediately'
}

function dataLayerEventSummary(value: unknown): string {
  return describeDataLayerTriggerEvent(
    value !== null && typeof value === 'object' ? (value as Partial<DataLayerTriggerEvent>) : undefined
  )
}

function placementSummary(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) {
    return 'No visual placement'
  }
  return value
    .map((rule) => {
      const position = rule.position === 'before' ? 'above' : rule.position === 'inside' ? 'inside' : 'below'
      return `${rule.domain || ''}${rule.path || 'No path'} · ${position} ${rule.selector || 'No selector'}`
    })
    .join('; ')
}

const dimensionVocabulary = (answers: 'Answers' | 'Segments'): FieldVocabulary => ({
  labels: {
    name: answers === 'Answers' ? 'Internal name' : 'Name',
    segments: answers,
    'segments.name': answers === 'Answers' ? 'Answer' : 'Segment',
    signals: 'Rules',
    isMultiWinning: 'Multiple segments',
    syncTo: 'Sync to',
    syncToCustomField: 'Custom field',
    syncMode: 'Sync as',
    syncMappings: 'Tags and lists'
  },
  hidden: { favorited: true, semanticType: true },
  values: { syncMode: { field: 'Custom field', tag: 'Tags' } },
  describers: { signals: describeSegmentRules }
})

const designLabels: Record<string, string> = {}
const designValues: Record<string, Record<string, string>> = {}
for (const section of designSections) {
  for (const field of section.fields) {
    designLabels[`options.${field.path}`] ??= `${section.title} › ${field.label}`
    const words = Object.fromEntries(
      (field.choices ?? []).flatMap((choice) => (choice.value === null ? [] : [[String(choice.value), choice.label]]))
    )
    if (Object.keys(words).length) {
      designValues[`options.${field.path}`] = words
    }
  }
}
for (const group of [...colorGroups, ...buttonGroups]) {
  for (const field of group.fields) {
    designLabels[`options.${field.path}`] = `${group.label} › ${field.label}`
  }
}
for (const [path, label] of Object.entries({ ...designLabels })) {
  if (path.startsWith('options.palette.')) {
    designLabels[path.replace('options.palette.', 'options.paletteDark.')] = `${label} (dark mode)`
  }
  designLabels[path.replace('options.', 'options.overrides.phone.')] = `${label} (phone)`
}
// Design labels already name their section, so the wrapper objects above them add nothing.
for (const path of Object.keys(designLabels)) {
  const keys = path.split('.')
  for (let depth = 2; depth < keys.length; depth++) {
    designLabels[keys.slice(0, depth).join('.')] ??= ''
  }
}
for (const device of DISPLAY_DEVICES) {
  designLabels[`options.displayTargets.${device.value}`] = device.label
}

const ACTION_TYPE_WORDS = {
  form: 'Submit a form',
  webhook: 'Send a webhook',
  list: 'Add to a list',
  tag: 'Add a tag',
  event: 'Trigger an event',
  automation: 'Start an automation',
  contact: 'Create or update a contact'
}

function actionType(value: unknown): string | undefined {
  if (value && typeof value === 'object' && 'type' in value && typeof value.type === 'string') {
    return value.type
  }
  return undefined
}

function actionVocabulary(type: string | undefined): FieldVocabulary {
  return {
    labels: {
      type: 'Action',
      settings: '',
      'settings.url': type === 'link' ? LINK_ACTION_LABELS.url : 'URL',
      'settings.open': LINK_ACTION_LABELS.open,
      'settings.method': 'HTTP method',
      'settings.customPayload': 'Extra data',
      'settings.eventMetadata': 'Event metadata',
      'settings.fields': 'Form fields',
      'settings.fields.label': 'Label',
      'settings.fields.placeholder': 'Placeholder',
      'settings.fields.required': 'Required',
      'settings.fields.width': 'Width'
    },
    hidden: { 'settings.actionId': true },
    values: { type: ACTION_TYPE_WORDS }
  }
}

const describeTriggeredAction: FieldDescriber = (before, after, names) =>
  describeFieldChanges(before, after, names, actionVocabulary(actionType(after) ?? actionType(before)))

// The analytics apps' listing names (`app/Apps/*/*App.php`), which label the Analytics tab's switches.
const ANALYTICS_TOOL_NAMES: Record<string, string> = {
  google_analytics: 'Google Analytics',
  google_tag_manager: 'Google Tag Manager',
  fathom_analytics: 'Fathom Analytics',
  posthog: 'PostHog'
}

// A tool without an entry reads as off, as its switch does on the Analytics tab.
function enabledTools(value: unknown): Set<string> {
  return new Set(
    (Array.isArray(value) ? value : []).flatMap((entry) => {
      const { name, enabled } = objectValue(entry)
      return typeof name === 'string' && enabled === true ? [name] : []
    })
  )
}

const describeAnalyticsTools: FieldDescriber = (before, after) => {
  const [was, now] = [enabledTools(before), enabledTools(after)]
  return [...new Set([...was, ...now])]
    .filter((tool) => was.has(tool) !== now.has(tool))
    .map((tool) => ({
      field: `Analytics tools › ${ANALYTICS_TOOL_NAMES[tool] ?? humanizeKey(tool)}`,
      before: was.has(tool) ? 'On' : 'Off',
      after: now.has(tool) ? 'On' : 'Off'
    }))
}

const VOCABULARIES: Record<string, FieldVocabulary> = {
  'Offers / Forms': {
    labels: {
      name: 'Name',
      isEnabled: 'Enabled',
      parts: '',
      'parts.title': 'Headline',
      'parts.description': 'Description',
      'parts.buttonText': 'Button text',
      'parts.imageUrl': 'Image',
      'parts.imageFocalPoint': 'Image focus',
      triggeredAction: ''
    },
    describers: { triggeredAction: describeTriggeredAction }
  },
  Flows: {
    labels: { name: 'Name', funnelDefinition: 'Steps' },
    hidden: { isSystemFlow: true },
    describers: { funnelDefinition: describeFlowSteps }
  },
  Widgets: {
    labels: {
      ...designLabels,
      name: 'Widget name',
      type: 'Widget type',
      isEnabled: 'Enabled',
      contentId: 'Flow',
      whenToAskNext: 'Question handling',
      visibility: '',
      'visibility.trigger': 'When it appears',
      'visibility.conditions': 'Targeting rules',
      'visibility.placements': 'Placement on page',
      'visibility.dismissRule': "After it's dismissed",
      'visibility.instantDismiss': 'When they click close',
      'visibility.formSubmitSelector': 'Form submissions',
      'visibility.dataLayerEvent': 'Google Tag Manager event',
      options: '',
      'options.placement': 'Placement',
      'options.displayTargets': 'Devices',
      'options.backButtonEnabled': 'Back button',
      'options.backButtonLabel': 'Back button label',
      'options.customCss': 'Custom CSS',
      'options.headerInfo.isEnabled': 'Show a header',
      'options.progressBar.isEnabled': 'Show a progress bar'
    },
    values: {
      ...designValues,
      'visibility.instantDismiss': { true: 'Dismiss right away', false: 'Minimize first' },
      'options.placement': { top: 'Top', bottom: 'Bottom', left: 'Bottom left', right: 'Bottom right' }
    },
    describers: {
      type: summaryDescriber('Widget type', (value) => widgetTypeLabel(String(value)) ?? String(value)),
      whenToAskNext: summaryDescriber(
        'Question handling',
        // Stored widget config already matches the schema's `whenToAskNext` enum.
        (value) =>
          FLOW_QUESTION_CHOICES.find((choice) => choice.value === flowQuestionChoice(value as Widget['whenToAskNext']))
            ?.label ?? 'Question handling'
      ),
      'visibility.trigger': summaryDescriber('When it appears', triggerSummary),
      'visibility.conditions': summaryDescriber('Targeting rules', (value) =>
        summarizePageTargeting({ conditions: (value ?? null) as TargetingCondition | null })
      ),
      'visibility.placements': summaryDescriber('Placement on page', placementSummary),
      'visibility.dismissRule': summaryDescriber("After it's dismissed", dismissSummary),
      'visibility.dataLayerEvent': summaryDescriber('Google Tag Manager event', dataLayerEventSummary)
    }
  },
  Campaigns: {
    labels: {
      name: 'Name',
      is_active: 'Live',
      base_url: 'Page URL',
      variants: 'Audiences',
      testing: 'Holdback'
    },
    hidden: { metadata: true, variants: true, original: true }
  },
  Goals: {
    labels: {
      name: 'Goal name',
      status: 'Status',
      value: 'Value per conversion',
      events: 'Events',
      'events.type': 'Type',
      'events.key': 'Event key',
      'events.page': 'Page'
    },
    values: {
      status: { active: 'Active', paused: 'Paused' },
      'events.type': { PAGEVIEW: 'Page view', CLICK: 'Click', SUBMIT: 'Form submit', JAVASCRIPT: 'JavaScript event' }
    }
  },
  Phrases: {
    labels: { fallbackText: 'Default text', map: 'Text per answer', segmentGroupId: 'Question' }
  },
  'Segment Groups': dimensionVocabulary('Segments'),
  Settings: {
    labels: {
      trackUtm: 'Track UTM parameters',
      emailWatcher: '',
      'emailWatcher.enabled': 'Email Watcher',
      analyticsSuppressionRules: 'Stop collecting',
      waitForConsent: 'Wait for consent before tracking',
      autoRefreshEnabled: 'Auto refresh project configuration data from server',
      jsHooks: '',
      'jsHooks.afterFormSubmit': 'JavaScript after a form submits',
      crossDomainTracking: 'Cross-domain tracking',
      crossDomainCookieBlacklist: 'Cross-domain cookie exclusions',
      espProfileCacheMinutes: 'ESP profile cache (minutes)',
      integrations: 'Analytics tools'
    },
    describers: { integrations: describeAnalyticsTools }
  },
  // Labels follow Settings › Forms & widgets; the two inverted flags read as the toggles there.
  'Widget Settings': {
    labels: {
      thanksForAnsweringText: 'Thank-you message',
      submittingText: 'Submitting text',
      disablePrefillFormFields: 'Pre-fill known email addresses',
      isBrandingHidden: '"Powered by RightMessage" badge'
    },
    values: {
      disablePrefillFormFields: { true: 'Off', false: 'On' },
      isBrandingHidden: { true: 'Hidden', false: 'Shown' }
    }
  },
  Attribution: {
    labels: {
      label: 'Label',
      value: 'Value',
      internal: 'Internal only',
      overwrite: 'Overwrite existing values',
      destination: 'Sync to'
    }
  }
}

/** One row per answer whose hints changed, named like `fill.dimensionIds` names its groups: never by segment ID. */
const describeFillHints: FieldDescriber = (before, after, names) => {
  const was = objectValue(before)
  const now = objectValue(after)
  return [...new Set([...Object.keys(was), ...Object.keys(now)])].flatMap((segmentId) => {
    const [previous = null, next = null] = [was[segmentId], now[segmentId]].map(
      (value) =>
        (Array.isArray(value) ? value : [])
          .map((phrase) => stripHtml(String(phrase)).replace(/\s+/g, ' ').trim())
          .filter(Boolean)
          .join(', ') || null
    )
    if (previous === next) {
      return []
    }
    return [{ field: `Answer hints › ${names.get(segmentId) ?? 'Removed answer'}`, before: previous, after: next }]
  })
}

const QUESTION_VOCABULARY: FieldVocabulary = {
  labels: {
    text: 'Question',
    name: 'Internal name',
    description: 'Description',
    displayType: 'Show answers as',
    options: 'Answers',
    'options.label': 'Answer',
    'options.conditions': 'Conditions for answer',
    'options.imageUrl': 'Image',
    otherOptions: '',
    'otherOptions.otherLabel': 'Other answer',
    isRandomizingOptionsOrder: 'Randomize answer order',
    dimensionId: 'Answers come from',
    syncTo: 'Sync to',
    syncToField: 'Sync field',
    displayOptions: '',
    'displayOptions.hideLabels': 'Hide answer labels',
    fill: 'Fill other questions',
    'fill.dimensionIds': 'Questions filled',
    'fill.minConfidence': 'How sure',
    'fill.hints': 'Answer hints',
    'fill.score': 'Score this answer',
    'fill.score.name': 'Adds points to',
    'fill.score.rules': 'Scoring rules',
    'fill.score.rules.text': 'Scoring rule',
    'fill.score.rules.points': 'Points'
  },
  hidden: { isQuestion: true },
  values: { displayType: { buttons: 'Buttons', radio: 'Radio', image: 'Images', list: 'Dropdown' } },
  describers: {
    'options.conditions': summaryDescriber('Conditions for answer', (value) => conditionSummary(value, 'None')),
    'fill.minConfidence': (before, after) => [
      {
        field: 'How sure',
        before: typeof before === 'number' ? fillConfidenceLabel(before) : null,
        after: typeof after === 'number' ? fillConfidenceLabel(after) : null
      }
    ],
    'fill.hints': describeFillHints
  }
}

/** Every config entity ID a field value can reference, mapped to its readable name. */
export function entityNames(...configs: (Config | null | undefined)[]): EntityNames {
  const names = new Map<string, string>()
  for (const config of configs) {
    const named = [
      ...(config?.rightCta?.offers ?? []),
      ...(config?.rightCta?.offerFunnels ?? []),
      ...(config?.rightCta?.widgets ?? []),
      ...(config?.campaigns ?? []),
      ...(config?.goals ?? []),
      ...(config?.dimensions ?? [])
    ]
    for (const entity of named) {
      if (entity.id && entity.name) {
        names.set(entity.id, stripHtml(entity.name).trim())
      }
    }
    const nameOptions = {
      segmentGroups: config?.dimensions ?? [],
      questions: (config?.rightAsk?.questions ?? []).map((question) => ({
        segmentGroupId: question.dimensionId,
        otherOptions: question.otherOptions
      }))
    }
    for (const segment of (config?.dimensions ?? []).flatMap((dimension) => dimension.segments ?? [])) {
      const name = segment.name === 'INT_OTHER' ? segmentName(segment, nameOptions) : segment.name
      if (segment.id && name) {
        names.set(segment.id, stripHtml(name).trim())
      }
    }
    for (const question of config?.rightAsk?.questions ?? []) {
      const text = stripHtml(question.text || '').trim()
      if (text) {
        names.set(question.id, text)
      }
    }
  }
  return names
}

/** A stored change's element as people recognize it on the page, such as Heading "Pricing for teams". */
export function storedElementLabel(action: StoredAction, config: Config): string {
  const fingerprint = action.targeting?.fingerprint
  const selector = action.targeting?.selector || action.selector || action.targeting?.fallbackSelector || ''
  const tag =
    fingerprint?.element?.tagName.toLowerCase() ||
    selector.match(/(?:^|[\s>+~])([a-z][a-z0-9]*)[^>+~]*$/i)?.[1]?.toLowerCase()
  const labels: Record<string, string> = {
    h1: 'Heading',
    h2: 'Heading',
    h3: 'Heading',
    h4: 'Heading',
    h5: 'Heading',
    h6: 'Heading',
    img: 'Image',
    button: 'Button',
    a: 'Link',
    p: 'Text',
    span: 'Text',
    div: 'Container',
    section: 'Section',
    input: 'Input',
    form: 'Form'
  }
  const label = (tag ? labels[tag] : undefined) ?? 'Element'
  const text = phrasePlainText(
    fingerprint?.text.content || fingerprint?.content?.textPrefix || '',
    config.phrases ?? []
  )
    .replace(/\s+/g, ' ')
    .trim()
  if (text) {
    return `${label} "${text.length > 36 ? `${text.slice(0, 35).trimEnd()}…` : text}"`
  }
  return label !== 'Element' ? label : phrasePlainText(selector, config.phrases ?? []) || label
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? Object.fromEntries(Object.entries(value)) : {}
}

function describeCampaign(
  before: Campaign | undefined,
  after: Campaign | undefined,
  current: Config,
  published: Config | null,
  names: EntityNames
): FieldChange[] {
  const rows =
    before && after ? describeFieldChanges(before, after, names, VOCABULARIES.Campaigns ?? { labels: {} }) : []
  const oldConfig = published ?? current
  const previousGroups = buildGroupCatalog(oldConfig)
  const currentGroups = buildGroupCatalog(current)
  const audienceName = (audience: Variant, old = false) =>
    audienceDisplayName(audience, old ? previousGroups : currentGroups)
  const targeting = (audience: Variant, old = false) => {
    const { operator, terms } = audienceTerms(audience)
    const groups = old ? previousGroups : currentGroups
    return (
      terms
        .map(
          (term) =>
            groups.find((group) => group.id === term.groupId)?.segments.find((segment) => segment.id === term.segmentId)
              ?.name ?? 'Deleted segment'
        )
        .join(operator === 'any' ? ' or ' : ' and ') || 'No audience selected'
    )
  }
  const row = (field: string, before: string | null = null, after: string | null = null) =>
    rows.push({ field, before, after })
  const testRows = (name: string, was: AudienceTest | undefined, test: AudienceTest | undefined) => {
    if (test?.state === 'running') {
      if (was?.state !== 'running' || was.id !== test.id) {
        row(`Started A/B test on ${name}`)
      } else if (was.run !== test.run) {
        row(`Restarted A/B test on ${name}`)
      }
    } else if (was?.state === 'running') {
      row(`Ended A/B test on ${name}`)
    } else if (test && !was) {
      row(`Set up A/B test on ${name}`)
    } else if (was && !test) {
      row(`Removed A/B test setup on ${name}`)
    }
    for (const version of test?.versions ?? []) {
      const oldVersion = was?.versions.find((item) => item.id === version.id)
      if (oldVersion && oldVersion.name !== version.name) {
        row(`${name}: version renamed`, versionLabel(was, version.id), versionLabel(test, version.id))
      }
      if (oldVersion && oldVersion.weight !== version.weight) {
        row(
          `${name} · ${versionLabel(test, version.id)}: traffic split changed`,
          `${oldVersion.weight}%`,
          `${version.weight}%`
        )
      }
      if (was && !oldVersion) {
        row(`${name} · ${versionLabel(test, version.id)}: version added`)
      }
    }
    if (test) {
      for (const version of was?.versions ?? []) {
        if (!test.versions.some((item) => item.id === version.id)) {
          row(`${name} · ${versionLabel(was, version.id)}: version removed`)
        }
      }
    }
  }
  const previous = new Map(before?.variants.map((audience) => [audience.id, audience]))
  const next = new Map(after?.variants.map((audience) => [audience.id, audience]))
  for (const audience of previous.values()) {
    if (!next.has(audience.id)) {
      row(`${audienceName(audience, true)}: audience removed`)
    }
  }
  for (const audience of next.values()) {
    const was = previous.get(audience.id)
    const name = audienceName(audience)
    if (!was) {
      row(`${name}: audience added`, null, targeting(audience))
    } else {
      if (was.name !== audience.name) {
        row('Audience renamed', audienceName(was, true), name)
      }
      if (JSON.stringify(was.rules) !== JSON.stringify(audience.rules)) {
        row(`${name}: targeting changed`, targeting(was, true), targeting(audience))
      }
    }
    testRows(name, was?.test, audience.test)
  }
  testRows(ORIGINAL_NAME, before?.original?.test, after?.original?.test)
  const wasDeployment = before?.original?.deployment
  const deployment = after?.original?.deployment
  if (deployment && (!wasDeployment || wasDeployment.testId !== deployment.testId)) {
    row(`Deployed ${deployment.name} on the original page`)
  } else if (wasDeployment && !deployment) {
    row(`Undeployed ${wasDeployment.name} from the original page`)
  }
  const kept = [...next.keys()].filter((id) => previous.has(id))
  const keptBefore = [...previous.keys()].filter((id) => next.has(id))
  if (kept.join('\n') !== keptBefore.join('\n')) {
    row(
      'Audience order changed',
      [...previous.values()]
        .filter((audience) => next.has(audience.id))
        .reverse()
        .map((audience) => audienceName(audience, true))
        .join(', '),
      [...next.values()]
        .filter((audience) => previous.has(audience.id))
        .reverse()
        .map((audience) => audienceName(audience))
        .join(', ')
    )
  }
  const versionActions = (campaign: Campaign | undefined, old = false) => {
    if (!campaign) {
      return []
    }
    const original = testNode(campaign, ORIGINAL_ID)
    return [
      ...(original ? [{ node: original, name: ORIGINAL_NAME }] : []),
      ...campaign.variants.map((audience) => ({ node: audience, name: audienceName(audience, old) }))
    ].flatMap(({ node, name }) =>
      audienceVersions(node, name).flatMap(({ versionId, label }) => {
        const occurrences = new Map<string, number>()
        return effectiveActions(node, versionId).map((action) => {
          const occurrence = occurrences.get(action.id) ?? 0
          occurrences.set(action.id, occurrence + 1)
          return {
            key: `${node.id}:${versionId}:${action.id}:${occurrence}`,
            baselineKey: `${node.id}:A:${action.id}:${occurrence}`,
            audienceId: node.id,
            versionId,
            label,
            action
          }
        })
      })
    )
  }
  const hasVersion = (campaign: Campaign | undefined, audienceId: string, versionId: string) =>
    Boolean(campaign && testNode(campaign, audienceId)?.test?.versions.some((version) => version.id === versionId))
  const oldActions = new Map(versionActions(before, true).map((entry) => [entry.key, entry]))
  const newActions = new Map(versionActions(after).map((entry) => [entry.key, entry]))
  for (const [key, entry] of oldActions) {
    if (!newActions.has(key) && (entry.versionId === 'A' || hasVersion(after, entry.audienceId, entry.versionId))) {
      row(`${storedElementLabel(entry.action, oldConfig)} · ${entry.label}: change removed`)
    }
  }
  for (const [key, entry] of newActions) {
    const was =
      oldActions.get(key)?.action ??
      (entry.versionId !== 'A' && !hasVersion(before, entry.audienceId, entry.versionId)
        ? oldActions.get(entry.baselineKey)?.action
        : undefined)
    const action = entry.action
    if (JSON.stringify(was) === JSON.stringify(action)) {
      continue
    }
    const label = `${storedElementLabel(action, current)} · ${entry.label}`
    const start = rows.length
    const old = was?.modifications ?? {}
    const now = action.modifications ?? {}
    const different = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b)
    if (different(old.text, now.text)) {
      row(
        `${label}: ${now.text === undefined ? 'text change removed' : 'text changed'}`,
        old.text ?? null,
        now.text ?? null
      )
    }
    if (different(old.findReplace, now.findReplace)) {
      row(`${label}: ${now.findReplace?.length ? 'words replaced' : 'word replacements removed'}`)
    }
    if (different(old.style, now.style) || different(old.classes, now.classes)) {
      row(`${label}: ${now.style || now.classes ? 'style changed' : 'style change removed'}`)
    }
    if (different(old.visibility, now.visibility)) {
      row(
        `${label}: ${now.visibility === 'hide' ? 'hidden' : now.visibility === 'show' ? 'shown' : 'visibility change removed'}`
      )
    }
    const oldAttributes = objectValue(old.attr)
    const attributes = objectValue(now.attr)
    for (const attribute of new Set([...Object.keys(oldAttributes), ...Object.keys(attributes)])) {
      if (!different(oldAttributes[attribute], attributes[attribute])) {
        continue
      }
      const noun = attribute === 'src' || attribute === 'srcset' ? 'image' : attribute === 'href' ? 'link' : 'attribute'
      const verb =
        attributes[attribute] === undefined
          ? `${noun} change removed`
          : noun === 'image'
            ? 'image replaced'
            : `${noun} changed`
      row(`${label}: ${verb}`)
    }
    if (was && different(was.page, action.page)) {
      const pages = (value: StoredAction) =>
        value.page
          .map((page) => `${page.domain === '*' ? '' : page.domain}${page.path} (${page.operator ?? 'equals'})`)
          .join('; ') || 'All pages'
      row(`${label}: page rules changed`, pages(was), pages(action))
    }
    if (was && (different(was.selector, action.selector) || different(was.targeting, action.targeting))) {
      row(`${label}: target changed`)
    }
    const known: Record<string, true> = {
      text: true,
      findReplace: true,
      style: true,
      classes: true,
      visibility: true,
      attr: true
    }
    if (
      rows.length === start ||
      [...Object.keys(old), ...Object.keys(now)].some((field) => !known[field] && different(old[field], now[field]))
    ) {
      row(`${label}: ${was ? 'change edited' : 'change added'}`)
    }
  }
  return rows
}

function hostedUrl(change: HostedConfigChange, path: string): string {
  return change.servingHost ? `${change.servingHost}${path}` : path
}

/** Rows for a hosted change, worded like the Pages index: where a page goes live, or how many pages a shared change reaches. */
export function describeHostedFields(change: HostedConfigChange): FieldChange[] {
  if (change.kind !== 'page') {
    return [
      {
        field: 'Affects',
        before: null,
        after: `${change.affectedPages} ${change.affectedPages === 1 ? 'page' : 'pages'}`
      }
    ]
  }
  if (change.path === null) {
    return []
  }
  if (change.type === 'added') {
    return [{ field: 'Goes live at', before: null, after: hostedUrl(change, change.path) }]
  }
  if (change.type === 'moved' && change.previousPath !== null) {
    return [{ field: 'URL', before: hostedUrl(change, change.previousPath), after: hostedUrl(change, change.path) }]
  }
  return []
}

/** The hosted page that owns a widget in the draft, or in the published config when the draft removed it. */
function widgetOwner(widgetId: string, current: Config | null, published: Config | null): string | null {
  const widget =
    current?.rightCta?.widgets?.find((item) => item.id === widgetId) ??
    published?.rightCta?.widgets?.find((item) => item.id === widgetId)
  return widget?.ownerPageId ?? null
}

/**
 * A widget a hosted page owns is part of that page, not the Widgets list: its change sits with the page
 * changes and reads "Form on page ‘Spring offer’". The ID stays the widget's, so its fields still describe it.
 */
export function pageOwnedWidgetChanges(
  changes: readonly ConfigChange[],
  current: Config | null,
  published: Config | null,
  pageNames: ReadonlyMap<string, string>
): ConfigChange[] {
  return changes.map((change) => {
    const pageId = change.category === 'Widgets' ? widgetOwner(change.id, current, published) : null
    if (!pageId) {
      return change
    }
    const pageName = pageNames.get(pageId)
    return { ...change, category: 'Pages', name: pageName ? `Form on page ‘${pageName}’` : 'Form on a page' }
  })
}

/**
 * Before → after rows for one modified change, worded like the dashboard UI.
 * Categories without a readable field model (split tests, integrations) have none.
 */
export function describeChangeFields(change: ConfigChange, current: Config, published: Config | null): FieldChange[] {
  if (change.category === 'Campaigns') {
    return describeCampaign(
      published?.campaigns.find((campaign) => campaign.id === change.id),
      current.campaigns.find((campaign) => campaign.id === change.id),
      current,
      published,
      entityNames(published, current)
    )
  }
  if (change.type !== 'modified' || !published) {
    return []
  }
  const names = entityNames(published, current)
  const byId = <T extends { id: string }>(list: readonly T[] | undefined) => list?.find((item) => item.id === change.id)
  const describe = (before: unknown, after: unknown, vocabulary: FieldVocabulary | undefined) =>
    vocabulary ? describeFieldChanges(before, after, names, vocabulary) : []

  switch (change.category) {
    case 'Offers / Forms':
      return describe(byId(published.rightCta?.offers), byId(current.rightCta?.offers), VOCABULARIES[change.category])
    case 'Flows':
      return describe(
        byId(published.rightCta?.offerFunnels),
        byId(current.rightCta?.offerFunnels),
        VOCABULARIES[change.category]
      )
    // Hosted page rows carry `hosted` and never reach here, so a Pages change here is a page's own widget.
    case 'Widgets':
    case 'Pages':
      return describe(byId(published.rightCta?.widgets), byId(current.rightCta?.widgets), VOCABULARIES.Widgets)
    case 'Goals':
      return describe(byId(published.goals), byId(current.goals), VOCABULARIES[change.category])
    case 'Phrases':
      return describe(byId(published.phrases), byId(current.phrases), VOCABULARIES[change.category])
    case 'Segment Groups':
      return describe(byId(published.dimensions), byId(current.dimensions), VOCABULARIES[change.category])
    case 'Questions': {
      const dimension = byId(current.dimensions)
      return dimension
        ? describe(byId(published.dimensions), dimension, dimensionVocabulary('Answers'))
        : describe(byId(published.rightAsk?.questions), byId(current.rightAsk?.questions), QUESTION_VOCABULARY)
    }
    case 'Settings':
      return describe(published.settings, current.settings, VOCABULARIES[change.category])
    case 'Widget Settings':
      return describe(published.rightCta?.settings, current.rightCta?.settings, VOCABULARIES[change.category])
    case 'Domains':
      // Added and removed domains are named rows of their own; only a new primary domain has a before → after.
      return change.id === 'domainNames.primary'
        ? [{ field: 'Primary domain', before: published.domainNames[0] ?? null, after: current.domainNames[0] ?? null }]
        : []
    case 'Attribution': {
      const index = Number(change.id)
      return describe(published.attributes?.[index], current.attributes?.[index], VOCABULARIES[change.category])
    }
    default:
      return []
  }
}
